import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAssessment } from '../../../contexts/AssessmentContext'
import { useOrg } from '../../../contexts/OrgContext'
import { useDemoPlan } from '../../../hooks/useDemoPlan'
import { useAIJobs } from '../../../contexts/AIJobsContext'
import { supabase } from '../../../shared/lib/supabase'

// Status badge styling
const getStatusStyle = (status) => {
  switch (status) {
    case 'retainer':
      return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
    case 'suspended':
      return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
    case 'discovery':
    default:
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
  }
}

const getStatusLabel = (status) => {
  switch (status) {
    case 'retainer':
      return 'Retainer'
    case 'suspended':
      return 'Suspended'
    case 'discovery':
    default:
      return 'Discovery'
  }
}

function Clients() {
  const { clients, loading, addClient, deleteClient } = useAssessment()
  const { currentOrg } = useOrg()
  const { isDemoOrg, clientCap: demoClientCap } = useDemoPlan()
  const { startJob } = useAIJobs()
  const [showNewClientForm, setShowNewClientForm] = useState(false)
  const [newClientName, setNewClientName] = useState('')
  const [newClientDomain, setNewClientDomain] = useState('')
  const [newClientContext, setNewClientContext] = useState('')
  const [capError, setCapError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const navigate = useNavigate()

  const clientCap = isDemoOrg ? demoClientCap : currentOrg?.client_cap
  const atCap = clientCap != null && clients.length >= clientCap

  const handleAddClient = async (e) => {
    e.preventDefault()
    if (atCap) {
      setCapError(isDemoOrg
        ? 'Demo accounts are limited to 2 clients. Contact support@sidewalksecurity.com to upgrade your plan.'
        : `Your organization has reached its client limit (${clientCap}). Contact your administrator to increase the cap.`
      )
      return
    }
    setCapError('')
    if (newClientName.trim()) {
      const client = await addClient({
        name: newClientName.trim(),
        domain: newClientDomain.trim() || null,
        enrichmentContext: newClientContext.trim() || null
      })
      if (client) {
        // Auto-trigger enrichment in background if domain was provided
        const domain = newClientDomain.trim()
        if (domain) {
          startJob(`client-enrich:${client.id}`, async () => {
            const { data: { session } } = await supabase.auth.getSession()
            const res = await fetch('/api/demo/enrich', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {})
              },
              body: JSON.stringify({
                companyName: client.name,
                domain,
                additionalContext: newClientContext.trim() || undefined
              })
            })
            if (!res.ok) throw new Error('Enrichment failed')
            const result = await res.json()
            // Save to DB
            await supabase.from('client_enrichment').upsert({
              client_id: client.id,
              enrichment: result.enrichment || {},
              dns: result.dns || {},
              ssl: result.ssl || {},
              whois: result.whois || {},
              ct_subdomains: result.ctSubdomains || [],
              shodan: result.shodan || {},
              generated_at: new Date().toISOString()
            }, { onConflict: 'client_id' })
            return result
          }).catch(err => console.error('[Auto-enrich] Failed:', err))
        }
        setNewClientName('')
        setNewClientDomain('')
        setNewClientContext('')
        setShowNewClientForm(false)
        navigate(`/clients/${client.id}`)
      }
    }
  }

  const handleDeleteClient = (clientId) => {
    if (window.confirm('Are you sure you want to delete this client and all their assessments?')) {
      deleteClient(clientId)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Clients</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage client organizations and their assessments</p>
        </div>
        <div className="flex items-center gap-3">
          {clientCap != null && (
            <span className={`text-sm font-medium ${atCap ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
              {clients.length} / {clientCap} clients
            </span>
          )}
          <button
            onClick={() => {
              if (atCap) {
                setCapError(`Your organization has reached its client limit (${clientCap}). Contact your administrator to increase the cap.`)
              }
              setShowNewClientForm(true)
            }}
            className={atCap ? 'btn-primary opacity-60 cursor-not-allowed' : 'btn-primary'}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Client
          </button>
        </div>
      </div>

      {/* New Client Form Modal */}
      {showNewClientForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Add New Client</h2>
            {capError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                {capError}
              </div>
            )}
            <form onSubmit={handleAddClient} className="space-y-4">
              <div>
                <label htmlFor="clientName" className="label">Organization Name</label>
                <input
                  type="text"
                  id="clientName"
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter organization name"
                  autoFocus
                />
              </div>
              <div>
                <label htmlFor="clientDomain" className="label">Primary Domain <span className="font-normal text-gray-400">(optional)</span></label>
                <input
                  type="text"
                  id="clientDomain"
                  value={newClientDomain}
                  onChange={(e) => setNewClientDomain(e.target.value)}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., acmefinancial.com"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Used for Client Intelligence enrichment</p>
              </div>
              <div>
                <label htmlFor="clientContext" className="label">Additional Context <span className="font-normal text-gray-400">(optional)</span></label>
                <input
                  type="text"
                  id="clientContext"
                  value={newClientContext}
                  onChange={(e) => setNewClientContext(e.target.value)}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., 200 employees, processes credit cards"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowNewClientForm(false); setCapError('') }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" disabled={atCap} className={atCap ? 'btn-primary opacity-50 cursor-not-allowed' : 'btn-primary'}>
                  Create Client
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Clients List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : clients.length === 0 ? (
        <div className="card p-12 text-center">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No clients yet</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">Get started by adding your first client organization.</p>
          <button onClick={() => setShowNewClientForm(true)} className="btn-primary">
            Add Your First Client
          </button>
        </div>
      ) : (
        <div>
          <div className="relative w-64 mb-4">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="card divide-y divide-gray-200 dark:divide-gray-700">
          {clients.filter(client => {
            if (!searchQuery) return true
            const q = searchQuery.toLowerCase()
            return (client.name || '').toLowerCase().includes(q) || (client.industry || '').toLowerCase().includes(q)
          }).map((client) => {
            const completedCount = client.assessments?.filter(a => a.status === 'completed').length || 0
            const inProgressCount = client.assessments?.filter(a => a.status === 'in_progress').length || 0

            return (
              <Link
                key={client.id}
                to={`/clients/${client.id}`}
                className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
              >
                <div className="flex items-center space-x-4 min-w-0">
                  {/* Client Logo / Icon */}
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden">
                    {client.logoUrl ? (
                      <img src={client.logoUrl} alt="" className="w-full h-full object-cover" />
                    ) : client.logoInitials || client.logoColor ? (
                      <div className="w-full h-full flex items-center justify-center text-sm font-bold text-white" style={{ backgroundColor: client.logoColor || '#0D1B2A' }}>
                        {client.logoInitials || (client.name || 'C').charAt(0).toUpperCase()}
                      </div>
                    ) : (
                      <div className="w-full h-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                        <svg className="w-5 h-5 text-primary-600 dark:text-accent-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Client Info */}
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors truncate">
                      {client.name}
                    </h3>
                    <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                      {client.industry && (
                        <>
                          <span className="truncate">{client.industry}</span>
                          <span>•</span>
                        </>
                      )}
                      <span>Added {new Date(client.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-4 flex-shrink-0">
                  {/* Client Status Badge */}
                  <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${getStatusStyle(client.status)}`}>
                    {getStatusLabel(client.status)}
                  </span>

                  {/* Assessment Counts */}
                  <div className="flex items-center space-x-3 text-sm">
                    {completedCount > 0 && (
                      <span className="flex items-center text-green-600 dark:text-green-400">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {completedCount}
                      </span>
                    )}
                    {inProgressCount > 0 && (
                      <span className="flex items-center text-amber-600 dark:text-amber-400">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {inProgressCount}
                      </span>
                    )}
                    {completedCount === 0 && inProgressCount === 0 && (
                      <span className="text-gray-400 dark:text-gray-500">No assessments</span>
                    )}
                  </div>

                  {/* Delete Button */}
                  <button
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleDeleteClient(client.id)
                    }}
                    className="p-2 text-gray-400 hover:text-danger-500 transition-colors opacity-0 group-hover:opacity-100"
                    title="Delete client"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>

                  {/* Arrow */}
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>
            )
          })}
          </div>
        </div>
      )}
    </div>
  )
}

export default Clients
